import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Topbar } from "@/components/parcelio/topbar";
import { NotificationDetailDialog } from "@/components/parcelio/notification-detail-dialog";
import { Button } from "@/components/ui/button";
import { notificationsApi, ApiError, type Notification } from "@/lib/api";
import { AlertTriangle, Info, Bell, CheckCheck } from "lucide-react";
import { cn, timeAgo } from "@/lib/utils";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

export default function Notifications() {
  const token = useAuth((s) => s.session?.accessToken);
  const [feed, setFeed] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Notification | null>(null);

  const load = () => {
    setLoading(true);
    notificationsApi
      .list(undefined, token)
      .then(setFeed)
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Could not load notifications"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const markRead = async (id: string) => {
    const prev = feed;
    const now = new Date().toISOString();
    setFeed((f) => f.map((n) => (n.id === id ? { ...n, read_at: now } : n)));
    setSelected((s) => (s && s.id === id ? { ...s, read_at: now } : s));
    try {
      await notificationsApi.markRead(id, token);
    } catch (err) {
      setFeed(prev);
      setSelected((s) => (s && s.id === id ? { ...s, read_at: null } : s));
      toast.error(err instanceof ApiError ? err.message : "Could not mark notification as read");
    }
  };

  const markAllRead = async () => {
    const unread = feed.filter((n) => !n.read_at);
    if (unread.length === 0) return;
    const prev = feed;
    const now = new Date().toISOString();
    setFeed((f) => f.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    try {
      await Promise.all(unread.map((n) => notificationsApi.markRead(n.id, token)));
      toast.success("All notifications marked read");
    } catch (err) {
      setFeed(prev);
      toast.error(err instanceof ApiError ? err.message : "Could not mark all as read");
    }
  };

  const unreadCount = feed.filter((n) => !n.read_at).length;

  return (
    <>
      <Topbar
        title="Notifications"
        subtitle={loading ? "Loading…" : `${unreadCount} unread`}
        actions={
          <Button variant="outline" size="sm" onClick={markAllRead}>
            <CheckCheck className="mr-1.5 h-4 w-4" /> Mark all read
          </Button>
        }
      />
      <div className="flex-1 p-4 md:p-6">
        <div className="surface-card mx-auto max-w-3xl">
          <ul className="divide-y">
            {loading ? <LoadingBlock label="Loading notifications…" /> : null}
            {!loading && feed.map((n) => {
              const read = n.read_at !== null;
              return (
                <li
                  key={n.id}
                  onClick={() => setSelected(n)}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 p-4 transition-colors hover:bg-muted/30",
                    !read && "bg-info/5"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                      n.severity === "critical" && "bg-destructive/10 text-destructive",
                      n.severity === "warning" && "bg-warning/15 text-warning-foreground",
                      n.severity === "info" && "bg-info/10 text-info"
                    )}
                  >
                    {n.severity === "critical" ? <AlertTriangle className="h-4 w-4" /> : n.severity === "warning" ? <Bell className="h-4 w-4" /> : <Info className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{n.title}</span>
                      {!read ? <span className="h-2 w-2 shrink-0 rounded-full bg-info" /> : null}
                    </div>
                    <p className="text-xs text-muted-foreground">{n.detail}</p>
                    <span className="mt-1 block text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                  </div>
                </li>
              );
            })}
            {!loading && feed.length === 0 ? (
              <li className="p-8 text-center text-sm text-muted-foreground">No notifications</li>
            ) : null}
          </ul>
        </div>
      </div>
      <NotificationDetailDialog
        notification={selected}
        onOpenChange={(open) => !open && setSelected(null)}
        onMarkRead={markRead}
      />
    </>
  );
}
